import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// PrimeReact components
import { Button, Dialog, InputNumber, InputSwitch, MultiSelect, ProgressBar, SegmentedControl, TextHelper, Tooltip } from '@allygpo/ally-ux';
import { useToast } from '../../context/ToastContext';

// Context and services
import { useScenarioContext, scenarioActions, FAVORITES_UI_CACHE_KEY, isGenerationRunActive } from '../../context/ScenarioContext';
import ApiService, { handleApiError } from '../../services/api';

// Types
import { ProductFamily, UserProductFamilies, ScenarioRequestWithMinsAndMaxAndPvt, PayerMixItem, ScenarioFormState, ProductFamilySelection, PayerGrouping, ExcludedOpportunity } from '../../types';

// Styles
import './ScenarioGenerator.css';
import { Card, Field, Modal, PageHeader, TabStrip, Tip, multiSelectSearch, notify } from '../../components/ui';
import { usePracticeData, getDisplayNameFromKey, CATEGORIES_SESSION_KEY, PRODUCT_FAMILIES_SESSION_KEY, EXCLUDED_OPPORTUNITIES_SESSION_KEY } from '../../hooks/usePracticeData';
import CategorySizeControl, { categoryUnitsLine } from './CategorySizeControl';
import ProductMixCard, { MixStatus, mixStatusMessage } from './ProductMixCard';
import PayerGroupingsTable, { PAYER_GROUPINGS_PER_PAGE } from './PayerGroupingsTable';
import EligibilityMatrix from './EligibilityMatrix';
import ExcludedOpportunitiesTable from './ExcludedOpportunitiesTable';

/** Helper text under the Optimization approach control, for the selected option. */
const APPROACH_HELP = {
  distribution: 'Spreads volume across products to explore different mixes',
  payer: 'Assigns each payer its most profitable product family',
};

const ScenarioGenerator: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  const navigate = useNavigate();
  // The app-wide toast (Layout), so messages survive navigation, e.g. generation finishing after Results opens.
  const toast = useToast();

  // Form state is now primarily driven by context, with local state for UI control
  const { generatorState } = state;
  const formData = useMemo(() => generatorState.form ?? {}, [generatorState.form]);

  const selectedProductFamilies = useMemo(() => generatorState.selectedFamilies ?? {}, [generatorState.selectedFamilies]);
  const proposedMixByCategory = useMemo(
    () => formData.proposed_product_mix_by_category ?? {},
    [formData.proposed_product_mix_by_category],
  );
  const proposedEnabledByCategory = useMemo(
    () => formData.proposed_mix_enabled_by_category ?? {},
    [formData.proposed_mix_enabled_by_category],
  );
  const userName = useMemo(() => generatorState.userName ?? null, [generatorState.userName]);
  // const useCurrentOnly = useMemo(() => generatorState.useCurrentOnly ?? false, [generatorState.useCurrentOnly]);

  // UI state
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { allCustomers, practiceMap, corporateParentSet, orgDropdownLocked, isInternal, isDemoMode } = usePracticeData();
  const hasAutoSelectedOrgRef = useRef(false);
  const [drugPayerMatrix, setDrugPayerMatrix] = useState<{ [drugId: string]: { [payer: string]: boolean } }>(formData.drugPayerMatrix ?? {});
  // Per-category volume scale factor (1.0 = 100% of last quarter's actual buy).
  // Seed from the cached map so size adjustments survive navigation back to this page.
  const [categoryScaleFactors, setCategoryScaleFactors] = useState<Record<string, number>>(
    () => generatorState.form?.category_scale_factor_map ?? {}
  );
  // Last quarter's actual total category units, fetched once per account+category and
  // cached so the size control shows the real modeled unit count from the start.
  // Keyed by `${account}||${category}`.
  const [baselineUnitsByCategory, setBaselineUnitsByCategory] = useState<Record<string, number>>({});
  const [excludedOpportunities, setExcludedOpportunities] = useState<ExcludedOpportunity[]>([]);
  const [showExcludedOpportunities, setShowExcludedOpportunities] = useState(false);
  // The practice has no therapeutic areas (the categories endpoint answers 404).
  const [noAreasForPractice, setNoAreasForPractice] = useState(false);
  const [selectedExcludedOpportunities, setSelectedExcludedOpportunities] = useState<ExcludedOpportunity[]>(formData.selectedExcludedOpportunities ?? []);
  // Track whether user has modified payer eligibility since opening dialog
  const [matrixDirty, setMatrixDirty] = useState(false);
  const originalMatrixRef = useRef<string>('');
  const originalExcludedOpportunitiesRef = useRef<ExcludedOpportunity[]>([]);
  const [payerMixEligible, setPayerMixEligible] = useState<boolean>(false);
  const [payerMixLoaded, setPayerMixLoaded] = useState<boolean>(false);
  const [showEligibilityMatrix, setShowEligibilityMatrix] = useState(false);
  // Toggle to display each payer's effective (eligibility-normalized) share inside matrix cells
  const [showEffectiveShare, setShowEffectiveShare] = useState(false);
  // Synchronized category state for both config and preview
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [payerMixByCategory, setPayerMixByCategory] = useState<Record<string, PayerMixItem[]>>({});
  // Active category's payer mix derived from the per-category map
  const payerMix = useMemo(() => (activeCategory ? payerMixByCategory[activeCategory] ?? [] : []), [activeCategory, payerMixByCategory]);
  // Payers with 0.0% are hidden from the eligibility matrix
  const visiblePayerMix = useMemo(() => payerMix.filter((p) => p.percent > 0), [payerMix]);
  const [payerGroupingsByCategory, setPayerGroupingsByCategory] = useState<Record<string, PayerGrouping[]>>({});
  // Active category's payer groupings derived from the per-category map
  const payerGroupings = useMemo(() => (activeCategory ? payerGroupingsByCategory[activeCategory] ?? [] : []), [activeCategory, payerGroupingsByCategory]);
  const [payerGroupingsLoading, setPayerGroupingsLoading] = useState(false);
  // Direct rebate / admin fee columns in the product mix. Opens when either was already set,
  // so saved overrides stay visible.
  const [showOverrides, setShowOverrides] = useState<boolean>(() =>
    Object.values(generatorState.selectedFamilies ?? {}).some((sel) => sel?.include && (Number(sel.adminFeePerEu ?? 0) !== 0 || Number(sel.directRebatePerEu ?? 0) !== 0))
  );

  // Generation mode
  // Checked = payor-based greedy generation; Unchecked = legacy generation
  const [generateByPayers, setGenerateByPayers] = useState<boolean>(false);

  // Fall back to distribution-based if payer eligibility is lost; don't auto-select payer-optimized when it becomes available.
  const prevPayerMixEligible = useRef(payerMixEligible);
  useEffect(() => {
    if (prevPayerMixEligible.current && !payerMixEligible) {
      setGenerateByPayers(false);
    }
    prevPayerMixEligible.current = payerMixEligible;
  }, [payerMixEligible]);

  // Restore the last-used generation mode (persisted in generatorState) exactly once,
  // and only after payer eligibility is confirmed. First login has no stored preference,
  // so it stays on distribution-based (the default). We never restore payer-based while
  // ineligible, and the one-shot ref ensures we don't fight the user's in-session toggles.
  const didRestoreGenerationMode = useRef(false);
  useEffect(() => {
    if (didRestoreGenerationMode.current) return;
    if (payerMixEligible && state.generatorState?.preferredGenerationMode === 'payer') {
      setGenerateByPayers(true);
      didRestoreGenerationMode.current = true;
    }
  }, [payerMixEligible, state.generatorState?.preferredGenerationMode]);

  const [payerDialogActiveTabIndex, setPayerDialogActiveTabIndex] = useState(0);
  // Page index (not row offset) on the Payer groupings tab.
  const [payerGroupingFirst, setPayerGroupingFirst] = useState(0);
  const [payerGroupingPageSize, setPayerGroupingPageSize] = useState(PAYER_GROUPINGS_PER_PAGE);
  const [payerGroupingHighlight, setPayerGroupingHighlight] = useState<string | null>(null);
  const [payerGroupingScrollId, setPayerGroupingScrollId] = useState<string | null>(null);

  // When the active category changes, reset the payer dialog to its default
  // view: the Eligibility Matrix tab and page 1 of the groupings table. A
  // stale page index (e.g. page 3 from a larger category) would break the
  // paginator if the newly selected category has fewer rows.
  useEffect(() => {
    setPayerDialogActiveTabIndex(0);
    setPayerGroupingFirst(0);
    setPayerGroupingHighlight(null);
    setPayerGroupingScrollId(null);
  }, [activeCategory]);

  // Add state to track loading status of payer preferences for each category
  const [payerPreferencesLoaded, setPayerPreferencesLoaded] = useState<Record<string, boolean>>({});

  const payerGroupList = useMemo(() => {
    if (!payerGroupings || payerGroupings.length === 0) return [];

    const grouped = new Map<string, string[]>();
    payerGroupings.forEach((item) => {
      const group = item.MasterPayorGroup?.trim() || 'Unknown';
      const payer = item.InsurancePayerPrimary?.trim() || 'Unknown';
      const existing = grouped.get(group) ?? [];
      if (!existing.includes(payer)) existing.push(payer);
      grouped.set(group, existing);
    });

    // Order master groups by their total payer share — same ordering the
    // eligibility matrix uses. payerMix is keyed by master payor group name,
    // so each group's share is a direct lookup (no summing across primaries).
    const shareByGroup = new Map<string, number>();
    payerMix.forEach((item) => {
      shareByGroup.set(item.payer.trim().toLowerCase(), item.percent);
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => {
        const shareA = shareByGroup.get(a.trim().toLowerCase()) ?? 0;
        const shareB = shareByGroup.get(b.trim().toLowerCase()) ?? 0;
        if (shareB !== shareA) return shareB - shareA; // larger share first
        return a.localeCompare(b, undefined, { sensitivity: 'base' }); // stable tie-break
      })
      .map(([group, payers], index) => ({ group, payers, rowId: `payer-groupings-row-${index}` }));
  }, [payerGroupings, payerMix]);

  const payersInGroupings = useMemo(() => {
    const set = new Set<string>();
    payerGroupings.forEach((item) => {
      const group = item.MasterPayorGroup?.trim().toLowerCase();
      if (group) set.add(group);
    });
    return set;
  }, [payerGroupings]);

  const jumpToPayerGrouping = (masterPayorGroupName: string) => {
    const hasGroupings = payerGroupings.length > 0;
    if (!hasGroupings) {
      toast.current?.show({
        severity: 'info',
        summary: 'No Groupings',
        detail: 'No payer groupings available for this organization.',
        life: 2500,
      });
      return;
    }

    const normalized = masterPayorGroupName.trim().toLowerCase();
    const exactIndex = payerGroupList.findIndex((g) => g.group.toLowerCase() === normalized);
    const containsIndex = payerGroupList.findIndex((g) => g.group.toLowerCase().includes(normalized));
    const targetIndex = exactIndex >= 0 ? exactIndex : containsIndex;

    // Switch to the Groupings tab (index 1) regardless; it helps user orient.
    setPayerDialogActiveTabIndex(1);

    if (targetIndex < 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Not Found',
        detail: `Couldn't find "${masterPayorGroupName}" in master payor groups.`,
        life: 3000,
      });
      return;
    }

    setPayerGroupingFirst(Math.floor(targetIndex / payerGroupingPageSize));

    const target = payerGroupList[targetIndex];
    setPayerGroupingHighlight(target.group);
    setPayerGroupingScrollId(target.rowId);
  };

  useEffect(() => {
    if (!showEligibilityMatrix) return;
    if (payerDialogActiveTabIndex !== 1) return;
    if (!payerGroupingScrollId) return;

    // Wait for tab + pagination render before scrolling.
    const id = payerGroupingScrollId;
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ block: 'center' });
    }, 50);

    return () => window.clearTimeout(t);
  }, [showEligibilityMatrix, payerDialogActiveTabIndex, payerGroupingFirst, payerGroupingScrollId]);

  useEffect(() => {
    if (hasAutoSelectedOrgRef.current) return;
    if (allCustomers.length === 0 || Object.keys(practiceMap).length === 0) return;
    if (formData.logged_in_user) {
      hasAutoSelectedOrgRef.current = true;
      return;
    }
    // Auto-select when locked (single org) OR when external user has a corporate
    // parent as their first entry (default to grandparent, keep dropdown selectable).
    const shouldAutoSelect = orgDropdownLocked ||
      (!isInternal && corporateParentSet.has(allCustomers[0]));
    if (!shouldAutoSelect) return;
    const firstKey = allCustomers[0];
    const accountNumber = practiceMap[firstKey];
    if (!firstKey || !accountNumber) return;
    hasAutoSelectedOrgRef.current = true;
    const displayName = getDisplayNameFromKey(firstKey);
    dispatch(scenarioActions.setGeneratorState({
      userName: displayName.trim(),
      form: { logged_in_user: accountNumber, categories: [], max_scenarios: 25 },
      selectedFamilies: {},
    }));
  }, [orgDropdownLocked, isInternal, allCustomers, practiceMap, corporateParentSet, formData.logged_in_user, dispatch]);

  // Load payer mix for a specific category
  const loadPayerMixByCategory = async (userId: string, category: string) => {
    setPayerMixLoaded(false);
    try {
      const response = await ApiService.getPayerMixByCategory({ logged_in_user: userId, category });
      if (response.mix && response.mix.length > 0) {
        setPayerMixByCategory((prev) => ({ ...prev, [category]: response.mix }));
        setPayerMixEligible(true);
        // Only load payer preferences for this category, passing the mix directly
        loadPayerPreferences(category, response.mix);
      } else {
        setPayerMixByCategory((prev) => ({ ...prev, [category]: [] }));
        setPayerMixEligible(false);
      }
    } catch (error) {
      console.error('Failed to load payer mix by category:', error);
      setPayerMixByCategory((prev) => ({ ...prev, [category]: [] }));
      setPayerMixEligible(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Payer Mix Error',
        detail: 'Could not load payer mix for this category. Generation will proceed with all payers eligible.',
        life: 4500,
      });
    } finally {
      setPayerMixLoaded(true);
    }
  };

  const loadPayerGroupings = async (userId: string, category: string) => {
    setPayerGroupingsLoading(true);
    try {
      const response = await ApiService.getPayerGroupingsByCategory(userId, category);
      const raw = response.data;
      // API returns { "MasterPayorGroup": ["payer1", "payer2"], ... }
      // Transform into PayerGrouping[] for the UI
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const rows: PayerGrouping[] = [];
        for (const group of Object.keys(raw)) {
          const payers = (raw as Record<string, string[]>)[group] ?? [];
          for (const payer of payers) {
            rows.push({ MasterPayorGroup: group, InsurancePayerPrimary: payer });
          }
        }
        setPayerGroupingsByCategory((prev) => ({ ...prev, [category]: rows }));
      } else {
        setPayerGroupingsByCategory((prev) => ({ ...prev, [category]: Array.isArray(raw) ? raw : [] }));
      }
    } catch (error) {
      console.error('Failed to load payer groupings:', error);
      setPayerGroupingsByCategory((prev) => ({ ...prev, [category]: [] }));
      toast.current?.show({
        severity: 'error',
        summary: 'Payer Groupings Error',
        detail: 'Could not load payer groupings.',
        life: 4500,
      });
    } finally {
      setPayerGroupingsLoading(false);
    }
  };

  // Ensure productFamiliesByCategory is declared before effects referencing it
  const [productFamiliesByCategory, setProductFamiliesByCategory] = useState<Record<string, ProductFamily[]>>({});
  const previousCategoriesRef = useRef<string[]>([]);

  const activeCategoryFamilies = useMemo(() => {
    const families = activeCategory ? (productFamiliesByCategory[activeCategory] || []) : [];
    return [...families].sort((a, b) => {
      const nameA = String(a.product_family_name ?? '').trim();
      const nameB = String(b.product_family_name ?? '').trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [activeCategory, productFamiliesByCategory]);

  // Last quarter's actual category units for the active category — the "Current"
  // (scenario_id 0) total shown in the preview. Feeds the category-size caption so
  // it can display the modeled unit count. Prefers the freshly fetched value, then
  // falls back to the current results in context; null when neither is available.
  const activeCategoryBaselineUnits = useMemo<number | null>(() => {
    if (!activeCategory) return null;
    const account = formData.logged_in_user;
    const key = account ? `${account}||${activeCategory}` : null;
    const fetched = key ? baselineUnitsByCategory[key] : undefined;
    if (fetched != null && fetched > 0) return fetched;
    const total = (state.scenarioResults ?? [])
      .filter((r) => Number(r.scenario_id) === 0 && r.category === activeCategory)
      .reduce((sum, r) => sum + Number(r.category_units || 0), 0);
    return total > 0 ? total : null;
  }, [activeCategory, formData.logged_in_user, baselineUnitsByCategory, state.scenarioResults]);

  // Fetch and cache last quarter's actual category units once per account+category,
  // so the modeled unit count is available before any scenario is generated.
  useEffect(() => {
    const account = formData.logged_in_user;
    if (!account || !activeCategory) return;
    const key = `${account}||${activeCategory}`;
    if (baselineUnitsByCategory[key] !== undefined) return; // already fetched
    let cancelled = false;
    (async () => {
      try {
        const units = await ApiService.getBaselineCategoryUnits(account, activeCategory);
        if (!cancelled) {
          setBaselineUnitsByCategory((prev) => ({ ...prev, [key]: units }));
        }
      } catch (err) {
        // Non-fatal: the caption falls back to the results-derived baseline or
        // the unit-less wording when the fetch fails.
        console.error('Failed to fetch baseline category units', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.logged_in_user, activeCategory, baselineUnitsByCategory]);

  const productFamiliesById = useMemo(() => {
    return Object.values(productFamiliesByCategory).flat().reduce<Record<string, ProductFamily>>((acc, family) => {
      acc[String(family.product_family_id)] = family;
      return acc;
    }, {});
  }, [productFamiliesByCategory]);

  // Payer-optimized generation is unsupported for oral inventory. Disable it when the active
  // category contains a product family with InventoryType = 'Oral', so switching categories
  // toggles the option (consistent with payerMixEligible, which is also per active category).
  const hasOralInventory = useMemo(() => {
    if (!activeCategory) return false;
    return (productFamiliesByCategory[activeCategory] ?? []).some(
      (pf) => (pf.inventory_type ?? '').trim().toLowerCase() === 'oral'
    );
  }, [activeCategory, productFamiliesByCategory]);

  // Oral inventory doesn't support payer-optimized generation; force distribution-based.
  useEffect(() => {
    if (hasOralInventory) {
      setGenerateByPayers(false);
    }
  }, [hasOralInventory]);

  // Initialize selections once product families are loaded per category.
  // Include only those with current_distribution > 0; all min distributions start at 0.
  useEffect(() => {
    const allFamilies = Object.values(productFamiliesByCategory).flat();
    if (allFamilies.length === 0) return;

    // Build or extend selection map without overwriting user changes.
    let changed = false;
    const updated: Record<string, ProductFamilySelection> = { ...selectedProductFamilies };
    allFamilies.forEach(pf => {
      if (!updated[pf.product_family_name]) {
        updated[pf.product_family_name] = {
          include: pf.current_distribution > 0,
          minDistribution: 0,
          proposedDistribution: pf.current_distribution ?? 0,
        };
        changed = true;
      } else {
        // If we previously auto-added and never interacted (minDistribution still undefined)
        if (updated[pf.product_family_name].minDistribution === undefined) {
          updated[pf.product_family_name].minDistribution = 0;
          changed = true;
        }
      }
    });
    if (changed) {
      dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updated }));
    }
  // Seeds selections when families load; it only adds missing entries, so edits to the
  // selections themselves must not re-run it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFamiliesByCategory, dispatch]);

  const updateProductMix = (family: ProductFamily, patch: Partial<ProductFamilySelection>) => {
    if (!activeCategory) return;
    // Same default as the seeding effect above. An input can report its value before that effect
    // runs (PrimeReact's InputNumber does on mount in development), so the entry may start here.
    const existing = selectedProductFamilies[family.product_family_name] || {
      include: family.current_distribution > 0,
      minDistribution: 0,
      proposedDistribution: family.current_distribution ?? 0,
    };
    const nextSelection = { ...existing, ...patch };
    const nextMix = {
      ...proposedMixByCategory,
      [activeCategory]: {
        ...(proposedMixByCategory[activeCategory] || {}),
        [family.product_family_id]: nextSelection.proposedDistribution ?? family.current_distribution ?? 0,
      },
    };
    // Merged into the current state, not a render-time copy, so back-to-back edits don't
    // overwrite each other.
    dispatch(scenarioActions.mergeGeneratorFamilies({ [family.product_family_name]: nextSelection }));
    dispatch(scenarioActions.mergeGeneratorForm({ proposed_product_mix_by_category: nextMix }));
  };

  const productMixValidation = useMemo(() => {
    return (formData.categories || []).map((category) => {
      const families = productFamiliesByCategory[category] || [];
      const included = families.filter((family) => selectedProductFamilies[family.product_family_name]?.include);
      const proposedOf = (family: ProductFamily) => (
        proposedMixByCategory[category]?.[family.product_family_id]
          ?? selectedProductFamilies[family.product_family_name]?.proposedDistribution
          ?? family.current_distribution
          ?? 0
      );
      const total = included.reduce((sum, family) => sum + proposedOf(family), 0);
      // The proposed mix is modeled only when the user turns it on for the area; only then
      // does the 100% check apply.
      const enabled = !!proposedEnabledByCategory[category];
      return { category, total, enabled, valid: included.length >= 2 && (!enabled || Math.abs(total - 1) < 0.0001) };
    });
  }, [formData.categories, productFamiliesByCategory, proposedMixByCategory, proposedEnabledByCategory, selectedProductFamilies]);

  const allProductMixesValid = productMixValidation.length > 0 && productMixValidation.every(({ valid }) => valid);



  useEffect(() => {
    if (formData.logged_in_user) {
      loadUserCategories();
      loadExcludedOpportunities(formData.logged_in_user);
    }
  // Loads once per account; the loaders are recreated every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.logged_in_user]);

  // A practice switch while this page is open: the form in context is already reset, but these
  // are seeded from it only on mount, so drop the previous practice's sizes, payer matrix and opportunities.
  const previousAccountRef = useRef(formData.logged_in_user);
  useEffect(() => {
    if (previousAccountRef.current === formData.logged_in_user) return;
    previousAccountRef.current = formData.logged_in_user;
    setCategoryScaleFactors({});
    setDrugPayerMatrix({});
    setSelectedExcludedOpportunities([]);
  }, [formData.logged_in_user]);

  // Pre-fill org name when logged_in_user is set (e.g. from Account Settings) but userName is empty
  useEffect(() => {
    if (formData.logged_in_user && !userName && Object.keys(practiceMap).length > 0) {
      const matchingKey = Object.entries(practiceMap).find(
        ([, acctNum]) => acctNum === formData.logged_in_user
      );
      if (matchingKey) {
        const displayName = getDisplayNameFromKey(matchingKey[0]);
        dispatch(scenarioActions.setGeneratorState({ userName: displayName }));
      }
    }
  }, [formData.logged_in_user, userName, practiceMap, dispatch]);


  useEffect(() => {
    if (formData.logged_in_user && formData.categories && formData.categories.length > 0) {
      const currentCategories = formData.categories;
      const previousCategories = previousCategoriesRef.current;

      // Find deselected categories
      const deselectedCategories = previousCategories.filter(cat => !currentCategories.includes(cat));

      if (deselectedCategories.length > 0) {
        // Clean up selections and product families for deselected categories
        const updatedSelections = { ...selectedProductFamilies };
        let selectionChanged = false;

        deselectedCategories.forEach((cat) => {
          // Remove product families from selections for this deselected category
          const familiesToRemove = productFamiliesByCategory[cat] || [];
          familiesToRemove.forEach((pf) => {
            if (updatedSelections[pf.product_family_name]) {
              delete updatedSelections[pf.product_family_name];
              selectionChanged = true;
            }
          });
        });

        if (selectionChanged) {
          dispatch(scenarioActions.setGeneratorState({ selectedFamilies: updatedSelections }));
        }

        // Remove product families state for deselected categories
        setProductFamiliesByCategory((prev) => {
          const updated = { ...prev };
          deselectedCategories.forEach((cat) => {
            delete updated[cat];
          });
          return updated;
        });

        // Remove payer mix and groupings for deselected categories
        setPayerMixByCategory((prev) => {
          const updated = { ...prev };
          deselectedCategories.forEach((cat) => { delete updated[cat]; });
          return updated;
        });
        setPayerGroupingsByCategory((prev) => {
          const updated = { ...prev };
          deselectedCategories.forEach((cat) => { delete updated[cat]; });
          return updated;
        });
      }

      // Update the ref for next comparison
      previousCategoriesRef.current = currentCategories;

      // Load product families, payer mix, and payer groupings for newly selected categories
      const userId = formData.logged_in_user;
      formData.categories.forEach((cat) => {
        // Only load if not already loaded
        if (!productFamiliesByCategory[cat]) {
          loadAllProductFamilies(cat);
        }
        if (!payerMixByCategory[cat]) {
          loadPayerMixByCategory(userId, cat);
        }
        if (!payerGroupingsByCategory[cat]) {
          loadPayerGroupings(userId, cat);
        }
      });
    } else if (formData.categories && formData.categories.length === 0) {
      // All categories deselected - clear everything
      previousCategoriesRef.current = [];
      setProductFamiliesByCategory({});
      setPayerMixByCategory({});
      setPayerGroupingsByCategory({});
      dispatch(scenarioActions.setGeneratorState({ selectedFamilies: {} }));
    }
  // Keyed on the account and the selected areas: the per-area caches it reads are what it
  // fills, so depending on them would re-run the loads it just started.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.logged_in_user, formData.categories]);

  // Set default active categories when selection changes
  useEffect(() => {
    if (formData.categories && formData.categories.length > 0) {
      if (!activeCategory || !formData.categories.includes(activeCategory)) {
        setActiveCategory(formData.categories[0]);
      }
    } else {
      setActiveCategory(null);
    }
  }, [formData.categories, activeCategory]);

  // Derive payerMixEligible from the active category's payer mix
  useEffect(() => {
    const mix = activeCategory ? payerMixByCategory[activeCategory] : undefined;
    setPayerMixEligible(!!mix && mix.length > 0);
    setPayerMixLoaded(mix !== undefined);
  }, [activeCategory, payerMixByCategory]);

  const loadUserCategories = async () => {
    if (!formData.logged_in_user) return;
    setNoAreasForPractice(false);
    try {
      const raw = sessionStorage.getItem(CATEGORIES_SESSION_KEY);
      if (raw) {
        const cache: Record<string, string[]> = JSON.parse(raw);
        if (cache[formData.logged_in_user]) {
          setAvailableCategories(cache[formData.logged_in_user]);
          return;
        }
      }
    } catch { /* ignore malformed cache */ }
    try {
      const response = await ApiService.getUserCategories(formData.logged_in_user);
      if (response.data?.categories) {
        setAvailableCategories(response.data.categories);
        try {
          const raw = sessionStorage.getItem(CATEGORIES_SESSION_KEY);
          const cache: Record<string, string[]> = raw ? JSON.parse(raw) : {};
          cache[formData.logged_in_user] = response.data.categories;
          sessionStorage.setItem(CATEGORIES_SESSION_KEY, JSON.stringify(cache));
        } catch { /* storage quota exceeded */ }
      }
    } catch (error) {
      // 404 means this practice simply has no therapeutic areas: an empty state, not an error.
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        setAvailableCategories([]);
        setNoAreasForPractice(true);
        return;
      }
      console.error('Failed to load categories:', (error as Error)?.message ?? error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load available categories',
        life: 3000,
      });
    }
  };

  const loadAllProductFamilies = async (category: string) => {
    if (!formData.logged_in_user || !category) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid Selection',
        detail: 'Cannot load product families without a valid account and therapeutic area.',
        life: 3000,
      });
      return;
    }
    try {
      const raw = sessionStorage.getItem(PRODUCT_FAMILIES_SESSION_KEY);
      if (raw) {
        const cache: Record<string, Record<string, UserProductFamilies>> = JSON.parse(raw);
        const cached = cache[formData.logged_in_user]?.[category];
        if (cached) {
          setProductFamiliesByCategory((prev) => ({ ...prev, [category]: cached.product_families }));
          dispatch(scenarioActions.setUserProductFamilies(cached));
          return;
        }
      }
    } catch { /* ignore malformed cache */ }
    try {
      const response = await ApiService.getCategoryProductFamiliesNames(
        formData.logged_in_user,
        category
      );
      if (response.data?.product_families) {
        setProductFamiliesByCategory((prev) => ({
          ...prev,
          [category]: response.data.product_families,
        }));
        dispatch(scenarioActions.setUserProductFamilies(response.data));
        try {
          const raw = sessionStorage.getItem(PRODUCT_FAMILIES_SESSION_KEY);
          const cache: Record<string, Record<string, UserProductFamilies>> = raw ? JSON.parse(raw) : {};
          if (!cache[formData.logged_in_user]) cache[formData.logged_in_user] = {};
          cache[formData.logged_in_user][category] = response.data;
          sessionStorage.setItem(PRODUCT_FAMILIES_SESSION_KEY, JSON.stringify(cache));
        } catch { /* storage quota exceeded */ }
      }
    } catch (error) {
      console.error(`Failed to load product families for ${category}:`, error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: handleApiError(error),
        life: 3000,
      });
    }
  };

  const loadPayerPreferences = async (category: string, mix?: PayerMixItem[]) => {
    if (!formData.logged_in_user) return;
    try {
      let families: ProductFamily[] | null = null;
      try {
        const raw = sessionStorage.getItem(PRODUCT_FAMILIES_SESSION_KEY);
        if (raw) {
          const cache: Record<string, Record<string, UserProductFamilies>> = JSON.parse(raw);
          families = cache[formData.logged_in_user]?.[category]?.product_families ?? null;
        }
      } catch { /* ignore malformed cache */ }

      if (families) {
        await loadDrugPayerMatrixForCategory(category, families, mix);
      } else {
        const response = await ApiService.getCategoryProductFamiliesNames(
          formData.logged_in_user,
          category
        );
        if (response.data?.product_families) {
          await loadDrugPayerMatrixForCategory(category, response.data.product_families, mix);
        }
      }
    } catch (error) {
      console.error(`Failed to load product families for ${category}:`, error);
    }
  }

  // Fetch payer eligibility for a category; default all to true if empty
  const loadDrugPayerMatrixForCategory = async (
    category: string,
    families: ProductFamily[],
    mix?: PayerMixItem[]
  ) => {
    if (!formData.logged_in_user) return;

    // Use provided mix or fall back to the category's stored mix
    const effectiveMix = mix ?? payerMixByCategory[category] ?? [];

    // Set loading state to false initially
    setPayerPreferencesLoaded((prev) => ({ ...prev, [category]: false }));

    try {
      const resp = await ApiService.getDrugPayerMatrix(formData.logged_in_user, category);
      const matrix = resp?.drugPayerMatrix || {};
      const expanded: { [drugId: string]: { [payer: string]: boolean } } = {};
      families.forEach((f) => {
        expanded[f.product_family_id] = matrix[f.product_family_id] || {};
      });

      families.forEach((f) => {
        // Set all payers to true by default, then override with loaded values if present
        const defaultRow: { [payer: string]: boolean } = {};
        effectiveMix.forEach((p) => {
          defaultRow[p.payer] = true;
        });
        expanded[f.product_family_id] = { ...defaultRow, ...(matrix[f.product_family_id] || {}) };
      });

      // Include any extra backend families not currently in list
      Object.keys(matrix).forEach((drugId) => {
        if (!expanded[drugId]) {
          expanded[drugId] = matrix[drugId];
        }
      });

      setDrugPayerMatrix((prev) => ({ ...prev, ...expanded }));

      // Set loading state to true once loaded
      setPayerPreferencesLoaded((prev) => ({ ...prev, [category]: true }));
    } catch (err) {
      console.error('Failed to load drug payer matrix for', category, err);

      // Fallback: initialize defaults if we have families
      const initialized: { [drugId: string]: { [payer: string]: boolean } } = {};
      families.forEach((f) => {
        const defaultRow: { [payer: string]: boolean } = {};
        effectiveMix.forEach((p) => {
          defaultRow[p.payer] = true;
        });
        initialized[f.product_family_id] = defaultRow;
      });

      setDrugPayerMatrix((prev) => ({ ...prev, ...initialized }));

      toast.current?.show({
        severity: 'error',
        summary: 'Eligibility Load Failed',
        detail: 'Using default (all eligible) payer matrix.',
        life: 4500,
      });

      // Set loading state to true even if fallback is used
      setPayerPreferencesLoaded((prev) => ({ ...prev, [category]: true }));
    }
  };

  const loadExcludedOpportunities = async (userId: string) => {
    try {
      const raw = sessionStorage.getItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY);
      if (raw) {
        const cache: Record<string, ExcludedOpportunity[]> = JSON.parse(raw);
        if (cache[userId]) {
          setExcludedOpportunities(cache[userId]);
          return;
        }
      }
    } catch { /* ignore malformed cache */ }
    try {
      const response = await ApiService.getExcludedOpportunities(userId);
      if (response.data) {
        const withIds = response.data.map((item) => ({
          ...item,
          unique_id: `${item.logged_in_user}-${item.category}-${item.contract_opportunities_id}-${item.product_family_id}-${item.tier}`,
        }));

        // Sort by Opportunity then Tier
        withIds.sort((a, b) => {
          const oppA = a.opportunity || '';
          const oppB = b.opportunity || '';
          const cmp = oppA.localeCompare(oppB);
          if (cmp !== 0) return cmp;
          return (a.tier || 0) - (b.tier || 0);
        });

        setExcludedOpportunities(withIds);
        try {
          const raw = sessionStorage.getItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY);
          const cache: Record<string, ExcludedOpportunity[]> = raw ? JSON.parse(raw) : {};
          cache[userId] = withIds;
          sessionStorage.setItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY, JSON.stringify(cache));
        } catch { /* storage quota exceeded */ }
      }
    } catch (error) {
      console.error('Failed to load excluded opportunities:', error);
      setExcludedOpportunities([]);
    }
  }

  // Backfill empty payer preference rows with 100% eligibility once payer mix is known
  useEffect(() => {
    const hasAnyMix = Object.values(payerMixByCategory).some((m) => m.length > 0);
    if (!hasAnyMix) return;
    const loadedCategories = Object.entries(payerPreferencesLoaded)
      .filter(([, isLoaded]) => isLoaded)
      .map(([cat]) => cat);
    if (loadedCategories.length === 0) return;

    setDrugPayerMatrix((prev) => {
      let mutated = false;
      const next: typeof prev = { ...prev };

      loadedCategories.forEach((cat) => {
        const catMix = payerMixByCategory[cat] ?? [];
        const families = productFamiliesByCategory[cat] || [];
        families.forEach((family) => {
          const familyId = family.product_family_id;
          const existingRow = next[familyId] ? { ...next[familyId] } : {};
          let rowChanged = !next[familyId];

          catMix.forEach((payer) => {
            if (!Object.prototype.hasOwnProperty.call(existingRow, payer.payer)) {
              existingRow[payer.payer] = true;
              rowChanged = true;
            }
          });

          if (rowChanged) {
            next[familyId] = existingRow;
            mutated = true;
          }
        });
      });

      return mutated ? next : prev;
    });
  }, [payerMixByCategory, payerPreferencesLoaded, productFamiliesByCategory]);

  // Update matrixReady logic to check loading state for the active category
  const [matrixReady, setMatrixReady] = useState(false);

  useEffect(() => {
    const ready =
      payerMix.length > 0 &&
      Object.keys(drugPayerMatrix).length > 0 &&
      !!activeCategory &&
      !!payerPreferencesLoaded[activeCategory];
    setMatrixReady(ready);
  }, [payerMix, drugPayerMatrix, activeCategory, payerPreferencesLoaded]);

  const validateForm = (data: Partial<ScenarioFormState> = formData): boolean => {
    const errors: Record<string, string> = {};

    if (!data.logged_in_user || !data.logged_in_user.trim()) {
      errors.logged_in_user = 'Account Number is required';
    }

    if (!data.categories || data.categories.length === 0) {
      errors.category = 'At least one therapeutic area is required';
    }

    if (!data.max_scenarios || data.max_scenarios < 1 || data.max_scenarios > 50) {
      errors.max_scenarios = 'Max scenarios must be between 1 and 50';
    }

    if (!allProductMixesValid) {
      const invalidAreas = productMixValidation.filter(({ valid }) => !valid).map(({ category }) => category);
      errors.product_mix = `Proposed mix must total 100% in: ${invalidAreas.join(', ')}`;
    }

    // Validate product families for each selected category
    if (
      !data.categories ||
      data.categories.some(
        (cat) =>
          !productFamiliesByCategory[cat] ||
          productFamiliesByCategory[cat].length === 0
      )
    ) {
      errors.product_families = 'No product families available for the selected account and category';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGenerateScenarios = async (mode?: 'payer' | 'legacy') => {
    const payerBased = mode ? mode === 'payer' : generateByPayers;
    const runId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let runStarted = false;
    const runDispatch: typeof dispatch = (action) => dispatch(scenarioActions.forRun(runId, action));
    const effectiveFormData: Partial<ScenarioFormState> = {
      ...formData,
      max_scenarios: payerBased ? (formData.max_scenarios ?? 25) : formData.max_scenarios,
    };

    if (!validateForm(effectiveFormData)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix the form errors before proceeding',
        life: 3000,
      });
      return;
    }

    // Determine eligible vs ineligible categories based on included product families (>=2 required)
    // A product family only counts as eligible if it is included AND has >0% max market share
    // (i.e. at least one payer is enabled for it)
    const fullMatrixForEligibility = buildFullDrugPayerMatrix();
    const eligibleCategories = (effectiveFormData.categories || []).filter(cat => {
      const pfs = productFamiliesByCategory[cat] || [];
      const catMix = payerMixByCategory[cat] ?? [];
      const effectiveCount = pfs.filter(pf => {
        if (!selectedProductFamilies[pf.product_family_name]?.include) return false;
        // Compute max market share for this PF in this category
        const row = fullMatrixForEligibility[pf.product_family_id] || {};
        const uncheckedPercent = catMix.reduce((sum, item) => {
          const isChecked = row[item.payer] ?? true;
          return !isChecked ? sum + item.percent : sum;
        }, 0);
        const maxShare = Math.max(0, 100 - uncheckedPercent);
        return maxShare > 0;
      }).length;
      return effectiveCount >= 2;
    });
    const ineligibleCategories = (effectiveFormData.categories || []).filter(cat => !eligibleCategories.includes(cat));

    if (ineligibleCategories.length > 0) {
      // Inform user which categories are skipped
      toast.current?.show({
        severity: 'warn',
        summary: 'Skipped Categories',
        detail: `Skipped: ${ineligibleCategories.join(', ')} (needs at least 2 product families).`,
        life: 6000,
      });
    }

    // Payer-optimized generation doesn't support oral inventory. Drop oral categories from a
    // payer run (they stay valid for distribution-based) and tell the user which were skipped.
    let runCategories = eligibleCategories;
    if (payerBased) {
      const oralExcludedCategories = eligibleCategories.filter(cat =>
        (productFamiliesByCategory[cat] ?? []).some(
          pf => (pf.inventory_type ?? '').trim().toLowerCase() === 'oral'
        )
      );
      if (oralExcludedCategories.length > 0) {
        runCategories = eligibleCategories.filter(cat => !oralExcludedCategories.includes(cat));
        toast.current?.show({
          severity: 'warn',
          summary: 'Skipped Categories',
          detail: `Skipped: ${oralExcludedCategories.join(', ')} (oral inventory is not supported by payer-optimized generation).`,
          life: 6000,
        });
      }
    }

    if (runCategories.length === 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'No Eligible Categories',
        detail: 'Select categories that have at least 2 included product families.',
        life: 4000,
      });
      return;
    }

    setIsGenerating(true);

    try {

      // Generate and calculate scenarios
      // Build min_distribution_map from selectedProductFamilies
      // Aggregate product families across all selected categories
      const aggregatedFamilies: ProductFamily[] = Object.values(productFamiliesByCategory).flat();
      const nameToFamily = new Map<string, ProductFamily>();
      aggregatedFamilies.forEach(pf => nameToFamily.set(pf.product_family_name, pf));

      const minDistMap: { [key: string]: number } = {};
      const feesMap: { [key: string]: number } = {};
      const directRebatesMap: { [key: string]: number } = {};
      Object.entries(selectedProductFamilies).forEach(([pfName, selection]) => {
        if (!selection.include) return; // skip excluded
        const pf = nameToFamily.get(pfName);
        if (pf) {
          // Store clamped minDistribution (0-1) using numeric id key; guard against float drift
          const raw = selection.minDistribution ?? 0;
          const clamped = Math.max(0, Math.min(1, Number(raw) || 0));
          minDistMap[pf.product_family_id.toString()] = clamped;
          if (typeof selection.adminFeePerEu === 'number') {
            feesMap[pf.product_family_id.toString()] = Math.round(selection.adminFeePerEu * 100) / 100;
          }
          // Clamped at 0 here too, not just in the input: a stale negative could survive in
          // cached generator state from before the input existed.
          if (typeof selection.directRebatePerEu === 'number') {
            directRebatesMap[pf.product_family_id.toString()] =
              Math.max(0, Math.round(selection.directRebatePerEu * 100) / 100);
          }
        }
      });
      // If no selections but we have families, default all included at 0 to satisfy API expectation
      if (Object.keys(minDistMap).length === 0 && aggregatedFamilies.length > 0) {
        aggregatedFamilies.forEach(pf => {
          minDistMap[pf.product_family_id.toString()] = 0;
        });
      }
      // Ensure we send a fully expanded matrix with defaults (true) for missing payer entries
      const fullMatrixForGeneration = buildFullDrugPayerMatrix();

      // Calculate max shares dynamically from the full matrix to ensure consistency
      // This fixes issues where stale or missing drugMaxShares caused zero-out in min_distribution_map
      const computedMaxSharesMap: Record<string, number> = {};
      Object.entries(productFamiliesByCategory).forEach(([cat, families]) => {
        const catMix = payerMixByCategory[cat] ?? [];
        families.forEach((pf) => {
          const row = fullMatrixForGeneration[pf.product_family_id] || {};
          const uncheckedPercent = catMix.reduce((sum, item) => {
            const isChecked = row[item.payer] ?? true;
            return !isChecked ? sum + item.percent : sum;
          }, 0);
          computedMaxSharesMap[pf.product_family_id] = Math.round(Math.max(0, 100 - uncheckedPercent) * 10) / 10;
        });
      });

      dispatch(scenarioActions.setGeneratorState({
        form: {
          ...effectiveFormData,
          drugPayerMatrix: fullMatrixForGeneration,
          // Cache admin fees (keyed by product_family_id) so they carry to the
          // results page and can be re-applied when recalculating alternates.
          product_family_fees_map: feesMap,
          product_family_direct_rebates_map: directRebatesMap,
          // Cache per-category volume scaling so alternate recalculations on the
          // results page model the same category size as the generated scenarios.
          category_scale_factor_map: categoryScaleFactors,
        } as any,
        lastRun: { mode: payerBased ? 'payer' : 'distribution', generatedAt: new Date().toISOString() },
      }));

      if (userName) {
        dispatch(scenarioActions.setCurrentOrg(userName))
      }
      if (effectiveFormData.logged_in_user) dispatch(scenarioActions.setCurrentUser(effectiveFormData.logged_in_user));
      if (effectiveFormData.categories && effectiveFormData.categories.length > 0) {
        dispatch(scenarioActions.setCurrentCategory(effectiveFormData.categories[0]));
      }

      // Initialize generation session only for eligible categories. Every update from this run
      // goes through runDispatch, so it is dropped if the practice changes or a newer run starts.
      dispatch(scenarioActions.initGenerationSession(runId, runCategories.length));
      runStarted = true;

      const categories = [...runCategories];
      if (categories.length === 0) throw new Error('No eligible categories after filtering');

      const firstCategory = categories.shift()!; // first eligible category

      // Build excludedScenarios from selectedExcludedOpportunities for this category
      const buildExcludedScenarios = (category: string) => {
        return selectedExcludedOpportunities
          .filter(opp => opp.category === category && !isProductFamilyNotIncluded(opp))
          .map(opp => ({
            logged_in_user: effectiveFormData.logged_in_user || '',
            category: opp.category || category,
            product_family_id: Number(opp.product_family_id),
            paid_on: opp.paid_on || '',
            rebate: Number(opp.rebate) ?? 0,
          }));
      };

      const buildPayload = (category: string): ScenarioRequestWithMinsAndMaxAndPvt => {
        const catFamilyIds = new Set(
          (productFamiliesByCategory[category] ?? [])
            .filter(pf => selectedProductFamilies[pf.product_family_name]?.include)
            .map(pf => pf.product_family_id.toString())
        );
        const catMinDistMap = Object.fromEntries(
          Object.entries(minDistMap).filter(([drugId]) =>
            catFamilyIds.has(drugId) && (computedMaxSharesMap[drugId] ?? 0) > 0
          )
        );
        const catMaxSharesMap = Object.fromEntries(
          Object.entries(computedMaxSharesMap).filter(([drugId]) => catFamilyIds.has(drugId.toString()))
        );
        const catMatrix = Object.fromEntries(
          Object.entries(formData.drugPayerMatrix ?? fullMatrixForGeneration)
            .filter(([drugId]) => catFamilyIds.has(drugId.toString()))
        );
        const catFeesMap = Object.fromEntries(
          Object.entries(feesMap).filter(([drugId]) => catFamilyIds.has(drugId))
        );
        const catDirectRebatesMap = Object.fromEntries(
          Object.entries(directRebatesMap).filter(([drugId]) => catFamilyIds.has(drugId))
        );
        // The area's proposed mix over its included families. Validation already requires it
        // to total 100%; normalizing here only absorbs floating-point drift.
        const proposedEntries = (productFamiliesByCategory[category] ?? [])
          .filter(pf => selectedProductFamilies[pf.product_family_name]?.include)
          .map(pf => [
            pf.product_family_id.toString(),
            Math.max(0, Number(
              proposedMixByCategory[category]?.[pf.product_family_id]
              ?? selectedProductFamilies[pf.product_family_name]?.proposedDistribution
              ?? pf.current_distribution
              ?? 0
            ) || 0),
          ] as [string, number]);
        const proposedTotal = proposedEntries.reduce((sum, [, v]) => sum + v, 0);
        const proposedEnabled = productMixValidation.find((m) => m.category === category)?.enabled ?? false;
        const catProposedMap = proposedEnabled && proposedTotal > 0
          ? Object.fromEntries(proposedEntries.map(([id, v]) => [id, Math.round((v / proposedTotal) * 1e6) / 1e6]))
          : undefined;
        return {
          logged_in_user: effectiveFormData.logged_in_user || '',
          category,
          max_scenarios: effectiveFormData.max_scenarios as any,
          min_distribution_map: catMinDistMap,
          product_family_fees_map: Object.keys(catFeesMap).length > 0 ? catFeesMap : undefined,
          product_family_direct_rebates_map: Object.keys(catDirectRebatesMap).length > 0 ? catDirectRebatesMap : undefined,
          drugPayerMatrix: catMatrix,
          drugMaxShares: catMaxSharesMap,
          excludedScenarios: buildExcludedScenarios(category),
          category_scale_factor: categoryScaleFactors[category] ?? 1.0,
          proposed_distribution_map: catProposedMap,
          batch_or_alternate: 'batch',
        };
      };

      localStorage.removeItem(FAVORITES_UI_CACHE_KEY); // Clear favorites cache on new generation

      const generateAndCalculate = payerBased
        ? ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvtGreedy
        : ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvt;

      // Generate first category fast
      const firstResponse = await generateAndCalculate(buildPayload(firstCategory));
      if (firstResponse.success) {
        const firstSummaries = (Array.isArray(firstResponse.summary) ? firstResponse.summary : [firstResponse.summary]).map(s => ({ ...s, category: firstCategory }));
        runDispatch(scenarioActions.setScenarioResults(firstResponse.results, firstSummaries));
        if (firstResponse.profitability) {
          runDispatch(scenarioActions.mergePayerProfitability(firstCategory, firstResponse.profitability));
        }
        if (firstResponse.rebate_details && firstResponse.rebate_details.length > 0) {
          runDispatch(scenarioActions.mergeRebateDetails(firstResponse.rebate_details));
        }
        if (firstResponse.excluded_payers && firstResponse.excluded_payers.length > 0 && firstResponse.excluded_payer_volume != null) {
          runDispatch(scenarioActions.setExcludedPayerInfo(firstCategory, firstResponse.excluded_payers, firstResponse.excluded_payer_volume));
        }
        if (firstResponse.missing_fee_schedule_pf_ids && firstResponse.missing_fee_schedule_pf_ids.length > 0) {
          runDispatch(scenarioActions.setMissingFeeSchedulePfIds(firstCategory, firstResponse.missing_fee_schedule_pf_ids));
        }
        if (firstResponse.fee_schedule_gaps && Object.keys(firstResponse.fee_schedule_gaps).length > 0) {
          runDispatch(scenarioActions.setFeeScheduleGaps(firstCategory, firstResponse.fee_schedule_gaps));
        }
        runDispatch(scenarioActions.incrementGenerationCompleted());
        // The practice changed (or a newer run started) while this area was generating.
        if (!isGenerationRunActive(runId)) return;

        // Only a multi-area run has anything left to continue with.
        toast.current?.show(categories.length > 0
          ? {
              severity: 'info',
              summary: 'Partial Results Ready',
              detail: `First therapeutic area (${firstCategory}) generated. Continuing with the remaining areas...`,
              life: 4000,
            }
          : {
              severity: 'success',
              summary: 'Scenarios generated',
              detail: `Finished generating ${firstCategory}`,
              life: 3000,
            });

        // Navigate immediately so user can see first batch
        navigate('/results');
      } else {
        throw new Error('Failed generating first competitive therapeutic area');
      }

      // Process remaining categories sequentially (background)
      for (const category of categories) {
        // Superseded: stop instead of generating (and announcing) areas nobody will see.
        if (!isGenerationRunActive(runId)) break;
        try {
          const resp = await generateAndCalculate(buildPayload(category));
          if (!isGenerationRunActive(runId)) break;
          if (resp.success) {
            const categorySummaries = (Array.isArray(resp.summary) ? resp.summary : [resp.summary]).map(s => ({ ...s, category }));
            runDispatch(
              scenarioActions.appendScenarioResults(
                resp.results,
                categorySummaries
              )
            );
            if (resp.profitability) {
              runDispatch(scenarioActions.mergePayerProfitability(category, resp.profitability));
            }
            if (resp.rebate_details && resp.rebate_details.length > 0) {
              runDispatch(scenarioActions.mergeRebateDetails(resp.rebate_details));
            }
            if (resp.excluded_payers && resp.excluded_payers.length > 0 && resp.excluded_payer_volume != null) {
              runDispatch(scenarioActions.setExcludedPayerInfo(category, resp.excluded_payers, resp.excluded_payer_volume));
            }
            if (resp.missing_fee_schedule_pf_ids && resp.missing_fee_schedule_pf_ids.length > 0) {
              runDispatch(scenarioActions.setMissingFeeSchedulePfIds(category, resp.missing_fee_schedule_pf_ids));
            }
            if (resp.fee_schedule_gaps && Object.keys(resp.fee_schedule_gaps).length > 0) {
              runDispatch(scenarioActions.setFeeScheduleGaps(category, resp.fee_schedule_gaps));
            }
            runDispatch(scenarioActions.incrementGenerationCompleted());
            toast.current?.show({
              severity: 'success',
              summary: 'Competitive Therapeutic Area Complete',
              detail: `Finished generating ${category}`,
              life: 2500,
            });
          }
        } catch (err) {
          console.error('Background competitive therapeutic area generation failed', err);
          toast.current?.show({
            severity: 'warn',
            summary: 'Partial Failure',
            detail: `Failed generating ${category}. Continuing...`,
            life: 3000,
          });
        }
      }

      if (categories.length > 0 && isGenerationRunActive(runId)) {
        toast.current?.show({
          severity: 'success',
          summary: 'All Categories Complete',
          detail: 'Finished generating scenarios for all categories.',
          life: 3000,
        });
      }

    } catch (error) {
      console.error('Scenario generation failed:', error);
      // A run superseded by a practice switch has nothing left to report.
      if (!runStarted || isGenerationRunActive(runId)) {
        toast.current?.show({
          severity: 'error',
          summary: 'Generation Failed',
          detail: handleApiError(error),
          life: 5000,
        });
      }
    } finally {
      // Also on failure, or the "Generating categories" banner never goes away. Scoped, so a
      // stale run can't end a newer one.
      if (runStarted) dispatch(scenarioActions.forRun(runId, scenarioActions.endGenerationSession()));
      setIsGenerating(false);
    }
  };

  const handleCategoryScaleChange = (category: string, nextFactor: number) => {
    const next = { ...categoryScaleFactors, [category]: nextFactor };
    setCategoryScaleFactors(next);
    // Persist immediately so the value survives navigation to the results page.
    dispatch(scenarioActions.mergeGeneratorForm({ category_scale_factor_map: next }));
  };

  const handleInputChange = (field: keyof ScenarioFormState, value: any) => {
    let updatedValue = value;
    // Safeguard: ensure categories is always an array
    if (field === 'categories') {
      updatedValue = Array.isArray(value) ? value : [];
    }

    dispatch(scenarioActions.mergeGeneratorForm({ [field]: updatedValue }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  useEffect(() => {
    if (!generateByPayers) return;
    // When payer-based generation is enabled, keep max scenarios valid even though the input is disabled.
    if (formData.max_scenarios === null || formData.max_scenarios === undefined) {
      dispatch(scenarioActions.mergeGeneratorForm({ max_scenarios: 25 }));
    }
  }, [generateByPayers, formData.max_scenarios, dispatch]);

  useEffect(() => {
    // Clear potentially stale max shares from previous sessions on mount
    if (Object.keys(formData.drugMaxShares || {}).length > 0) {
      dispatch(scenarioActions.mergeGeneratorForm({ drugMaxShares: {} }));
    }

    // On mount, if we have a cached user but no categories, reload them.
    if (formData.logged_in_user && availableCategories.length === 0) {
      loadUserCategories();
    }
  // Mount only, by design.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const categoryOptions = useMemo(() => availableCategories.map(cat => ({
    label: cat,
    value: cat,
  })), [availableCategories]);

  // Product families to show in Drug ↔ Payer Eligibility dialog (only active category)
  const eligibilityFamilies = useMemo(() => {
    const families = activeCategory ? (productFamiliesByCategory[activeCategory] || []) : [];
    return [...families].sort((a, b) => {
      const nameA = String(a.product_family_name ?? '').trim();
      const nameB = String(b.product_family_name ?? '').trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [activeCategory, productFamiliesByCategory]);

  // Toggle payers for a single product family row
  const toggleRowAll = (pfId: number | string, nextValue: boolean) => {
    const id = String(pfId);
    setDrugPayerMatrix((prev) => {
      const row = { ...(prev[id] || {}) };
      visiblePayerMix.forEach((item) => {
        row[item.payer] = nextValue;
      });
      return { ...prev, [id]: row };
    });
    setMatrixDirty(true);
  };

  // Check if every cell in the matrix is selected (for all eligible families × all payers)
  const isEntireMatrixSelected = useMemo(() => {
    if (!matrixReady || eligibilityFamilies.length === 0 || visiblePayerMix.length === 0) return false;
    return eligibilityFamilies.every((pf) => {
      const isSelected = selectedProductFamilies[pf.product_family_name]?.include ?? true;
      if (!isSelected) return true; // skip disabled rows
      return visiblePayerMix.every((item) => drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true);
    });
  }, [matrixReady, eligibilityFamilies, visiblePayerMix, drugPayerMatrix, selectedProductFamilies]);

  // Toggle all payers for all product families in the matrix
  const toggleAllMatrix = (nextValue: boolean) => {
    setDrugPayerMatrix((prev) => {
      const next = { ...prev };
      eligibilityFamilies.forEach((pf) => {
        const isSelected = selectedProductFamilies[pf.product_family_name]?.include ?? true;
        if (!isSelected) return;
        const row = { ...(next[pf.product_family_id] || {}) };
        visiblePayerMix.forEach((item) => {
          row[item.payer] = nextValue;
        });
        next[pf.product_family_id] = row;
      });
      return next;
    });
    setMatrixDirty(true);
  };

  // Build a fully expanded matrix across all loaded product families & payers
  const buildFullDrugPayerMatrix = (): { [drugId: string]: { [payer: string]: boolean } } => {
    const full: { [drugId: string]: { [payer: string]: boolean } } = {};
    Object.entries(productFamiliesByCategory).forEach(([cat, families]) => {
      const catMix = payerMixByCategory[cat] ?? [];
      families.forEach(f => {
        const existingRow = drugPayerMatrix[f.product_family_id] || {};
        const row: { [payer: string]: boolean } = {};
        catMix.forEach(p => {
          row[p.payer] = Object.prototype.hasOwnProperty.call(existingRow, p.payer) ? existingRow[p.payer] : true;
        });
        full[f.product_family_id] = row;
      });
    });
    return full;
  };

  // Helper: compute drugMaxShares from a given matrix for provided families using a given payer mix
  const computeDrugMaxShares = (
    matrix: { [drugId: string]: { [payer: string]: boolean } },
    families: ProductFamily[],
    mix: PayerMixItem[] = payerMix
  ): Record<string, number> => {
    return families
      .map((pf) => {
        const uncheckedPercent = mix.reduce((acc, item) => {
          const isChecked = matrix[pf.product_family_id]?.[item.payer] ?? true;
          return !isChecked ? acc + item.percent : acc;
        }, 0);
        // Clamp to [0, 100] and round to one decimal
        const maxMarketShare = Math.max(0, Math.min(100, 100 - uncheckedPercent));
        return { productFamilyId: pf.product_family_id, maxMarketShare: Math.round(maxMarketShare * 10) / 10 };
      })
      .reduce<Record<string, number>>((acc, d) => {
        acc[d.productFamilyId] = d.maxMarketShare;
        return acc;
      }, {});
  };

  // Keep a fully expanded matrix in formData for generation even if user cancels dialog
  useEffect(() => {
    // Need at least one category with a loaded payer mix
    const hasAnyMix = Object.values(payerMixByCategory).some((m) => m.length > 0);
    if (!hasAnyMix) return;
    const full = buildFullDrugPayerMatrix();
    // Compute max shares per category then merge
    const nextMaxShares: Record<string, number> = {};
    Object.entries(productFamiliesByCategory).forEach(([cat, families]) => {
      const catMix = payerMixByCategory[cat] ?? [];
      if (families.length > 0 && catMix.length > 0) {
        const catShares = computeDrugMaxShares(full, families, catMix);
        Object.assign(nextMaxShares, catShares);
      }
    });

    dispatch(scenarioActions.mergeGeneratorForm({ drugPayerMatrix: full, drugMaxShares: nextMaxShares }));
  // The matrix helpers are recreated every render; the inputs they read are listed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drugPayerMatrix, payerMixByCategory, productFamiliesByCategory, dispatch]);

  const isProductFamilyNotIncluded = (data: ExcludedOpportunity): boolean => {
    if (data.product_family_id !== undefined && data.product_family_id !== null) {
      const family = productFamiliesById[String(data.product_family_id)];
      if (family) {
        return !(selectedProductFamilies[family.product_family_name]?.include ?? false);
      }
    }

    if (!data.product_family_name) return false;
    const targetName = data.product_family_name.trim();
    const selectedKey = Object.keys(selectedProductFamilies).find(
      (name) => name.trim() === targetName
    );
    return !(selectedKey && selectedProductFamilies[selectedKey]?.include);
  };

  const pruneDisabledExcludedOpportunities = (
    nextSelectedFamilies: Record<string, ProductFamilySelection> = selectedProductFamilies
  ) => {
    if (selectedExcludedOpportunities.length === 0) return;

    const filteredSelectedOpportunities = selectedExcludedOpportunities.filter((opp) => {
      if (opp.product_family_id !== undefined && opp.product_family_id !== null) {
        const family = productFamiliesById[String(opp.product_family_id)];
        if (family) {
          return nextSelectedFamilies[family.product_family_name]?.include ?? false;
        }
      }

      if (!opp.product_family_name) return true;
      const targetName = opp.product_family_name.trim();
      const selectedKey = Object.keys(nextSelectedFamilies).find(
        (name) => name.trim() === targetName
      );
      return selectedKey ? (nextSelectedFamilies[selectedKey]?.include ?? false) : false;
    });

    if (filteredSelectedOpportunities.length === selectedExcludedOpportunities.length) return;

    setSelectedExcludedOpportunities(filteredSelectedOpportunities);
    dispatch(scenarioActions.mergeGeneratorForm({ selectedExcludedOpportunities: filteredSelectedOpportunities }));
  };

  useEffect(() => {
    pruneDisabledExcludedOpportunities(selectedProductFamilies);
  // The pruner is recreated every render; the state it reads is listed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedExcludedOpportunities, selectedProductFamilies]);

  const isExcludedOpportunitySelectable = (rowData: ExcludedOpportunity): boolean => {
    // If product family is not selected in Advanced Settings, disable it
    if (isProductFamilyNotIncluded(rowData)) {
      return false;
    }
    // If this row is already selected, it's always selectable (to allow deselection)
    if (selectedExcludedOpportunities.some(opp => opp.unique_id === rowData.unique_id)) {
      return true;
    }
    // Check if any other tier from the same contract opportunity is already selected
    const hasOtherTierSelected = selectedExcludedOpportunities.some(
      opp => opp.contract_opportunities_id === rowData.contract_opportunities_id && opp.unique_id !== rowData.unique_id
    );
    return !hasOtherTierSelected;
  };

  const getDisabledOpportunityTooltip = (rowData: ExcludedOpportunity): string => {
    if (isProductFamilyNotIncluded(rowData)) {
      return `The product family "${rowData.product_family_name}" hasn't been selected for generation`;
    }
    return "Another tier for this opportunity is already selected";
  };

  const wrapWithOpportunityTooltip = (content: React.ReactNode, rowData: ExcludedOpportunity) => {
    const disabled = !isExcludedOpportunitySelectable(rowData);
    const exclusionReason = rowData.exclusion_reason?.trim();
    // A row that can't be selected says why, and still shows why it isn't modeled.
    const tooltip = disabled
      ? `${getDisabledOpportunityTooltip(rowData)}${exclusionReason ? `

Exclusion reason: ${exclusionReason}` : ''}`
      : exclusionReason || undefined;
    return (
      <span
        className="excl-opp-cell block"
        data-pr-tooltip={tooltip}
        style={{ display: 'block', pointerEvents: disabled ? 'auto' : undefined }}
        onClick={disabled ? (e) => e.stopPropagation() : undefined}
      >
        {content}
      </span>
    );
  };

  const applyOpportunities = (selectedOpportunities: ExcludedOpportunity[]) => {
    const enabledOpportunities = selectedOpportunities.filter((opp) => !isProductFamilyNotIncluded(opp));
    setSelectedExcludedOpportunities(enabledOpportunities);
    setShowExcludedOpportunities(false);
    dispatch(scenarioActions.mergeGeneratorForm({ selectedExcludedOpportunities: enabledOpportunities }));
    notify(toast, 'Excluded opportunities updated');
  }


  const hasAnyEligibleCategory = useMemo(() => (formData.categories || []).some((category) => {
    const families = productFamiliesByCategory[category] || [];
    const categoryPayers = payerMixByCategory[category] ?? [];
    return families.filter((family) => {
      if (!selectedProductFamilies[family.product_family_name]?.include) return false;
      if (categoryPayers.length === 0) return true;
      const payerRow = drugPayerMatrix[family.product_family_id] || {};
      const disabledPercent = categoryPayers.reduce((total, payer) => total + (!(payerRow[payer.payer] ?? true) ? payer.percent : 0), 0);
      return 100 - disabledPercent > 0;
    }).length >= 2;
  }), [formData.categories, productFamiliesByCategory, payerMixByCategory, selectedProductFamilies, drugPayerMatrix]);

  const areaCount = formData.categories?.length ?? 0;
  // The remaining areas of a run keep generating after the page navigates to Results (and this
  // component unmounts), so a remount's isGenerating is false while that run is still going.
  // A second run then would interleave its results with the first.
  const earlierRunInProgress = !isGenerating && state.generationStatus?.inProgress === true;
  const generateDisabled = isGenerating || earlierRunInProgress || !hasAnyEligibleCategory || !allProductMixesValid || payerGroupingsLoading || (!matrixReady && payerMixEligible);

  const approachLabel = generateByPayers ? 'Payer optimized' : 'Distribution';
  const areasLabel = (formData.categories ?? []).join(', ') || 'No therapeutic area';
  const maxScenarios = formData.max_scenarios ?? 25;
  const activeFactor = activeCategory ? (categoryScaleFactors[activeCategory] ?? 1.0) : 1.0;
  const unitsLine = categoryUnitsLine(activeCategoryBaselineUnits, activeFactor);

  // Mix status per area, for the tabs, the totals row and the footer warning.
  const mixStatusByCategory: Record<string, MixStatus> = {};
  productMixValidation.forEach(({ category, total, valid, enabled }) => {
    const included = (productFamiliesByCategory[category] || []).filter((f) => selectedProductFamilies[f.product_family_name]?.include).length;
    mixStatusByCategory[category] = { category, total, valid, included, enabled };
  });
  const setProposedEnabled = (enabled: boolean) => {
    if (!activeCategory) return;
    dispatch(scenarioActions.mergeGeneratorForm({ proposed_mix_enabled_by_category: { ...proposedEnabledByCategory, [activeCategory]: enabled } }));
  };
  const firstInvalidMix = productMixValidation.find(({ valid }) => !valid);
  const invalidMixLine = firstInvalidMix
    ? `${areaCount > 1 ? `${firstInvalidMix.category} proposed mix` : 'Proposed mix'} is ${Math.round(firstInvalidMix.total * 10000) / 100}% · ${mixStatusMessage(mixStatusByCategory[firstInvalidMix.category])}`
    : null;

  // "Reset to current" appears once the active area's mix differs from today's.
  const mixEdited = activeCategoryFamilies.some((family) => {
    const selection = selectedProductFamilies[family.product_family_name];
    const proposed = proposedMixByCategory[activeCategory ?? '']?.[family.product_family_id] ?? selection?.proposedDistribution ?? family.current_distribution ?? 0;
    return (selection?.include ?? false) !== (family.current_distribution > 0) || Math.abs(proposed - (family.current_distribution ?? 0)) > 1e-6;
  });
  const resetMixToCurrent = () => {
    if (!activeCategory) return;
    const patch: Record<string, Partial<ProductFamilySelection>> = {};
    const mix: Record<string, number> = {};
    activeCategoryFamilies.forEach((family) => {
      patch[family.product_family_name] = { include: family.current_distribution > 0, proposedDistribution: family.current_distribution ?? 0 };
      mix[family.product_family_id] = family.current_distribution ?? 0;
    });
    dispatch(scenarioActions.mergeGeneratorFamilies(patch));
    dispatch(scenarioActions.mergeGeneratorForm({ proposed_product_mix_by_category: { ...proposedMixByCategory, [activeCategory]: mix } }));
  };

  // Tile statuses: eligibility restrictions and excluded opportunities for the active area.
  const eligibilityRestrictions = eligibilityFamilies
    .filter((pf) => selectedProductFamilies[pf.product_family_name]?.include)
    .reduce((count, pf) => count + visiblePayerMix.filter((item) => !(drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true)).length, 0);
  const areaOpportunities = excludedOpportunities.filter((opp) => activeCategory && opp.category === activeCategory);
  const areaIncluded = selectedExcludedOpportunities.filter((opp) => opp.category === activeCategory).length;
  const eligibilityTileDisabled = !payerMixLoaded || !payerMixEligible || !matrixReady || hasOralInventory;

  const plural = (n: number, word: string, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

  // Clear form: back to an empty run for the practice in the top bar (the practice itself stays).
  const formHasInput = areaCount > 0 || (formData.max_scenarios ?? 25) !== 25 || selectedExcludedOpportunities.length > 0;
  const clearForm = () => {
    setMatrixReady(false);
    setPayerPreferencesLoaded({});
    dispatch(scenarioActions.setGeneratorState({
      form: {
        logged_in_user: formData.logged_in_user,
        categories: [],
        max_scenarios: 25,
        selectedExcludedOpportunities: [],
        drugPayerMatrix: {},
        drugMaxShares: {},
        category_scale_factor_map: {},
        proposed_product_mix_by_category: {},
        proposed_mix_enabled_by_category: {},
      },
      selectedFamilies: {},
    }));
    setProductFamiliesByCategory({});
    setSelectedExcludedOpportunities([]);
    setDrugPayerMatrix({});
    setCategoryScaleFactors({});
    setValidationErrors({});
    setPayerMixByCategory({});
    setPayerGroupingsByCategory({});
    setPayerMixEligible(false);
    setPayerMixLoaded(false);
    setShowOverrides(false);
  };

  const openEligibility = () => {
    if (eligibilityTileDisabled) return;
    originalMatrixRef.current = JSON.stringify(drugPayerMatrix);
    setMatrixDirty(false);
    setPayerDialogActiveTabIndex(0);
    setShowEligibilityMatrix(true);
  };

  const openExcluded = () => {
    originalExcludedOpportunitiesRef.current = [...selectedExcludedOpportunities];
    setShowExcludedOpportunities(true);
  };

  // Unsaved eligibility edits, cell by cell against the snapshot taken when the modal opened.
  const matrixChangeCount = (() => {
    if (!showEligibilityMatrix) return 0;
    let original: Record<string, Record<string, boolean>> = {};
    try { original = JSON.parse(originalMatrixRef.current || '{}'); } catch { /* snapshot unreadable */ }
    let n = 0;
    eligibilityFamilies.forEach((pf) => visiblePayerMix.forEach((item) => {
      const before = original[pf.product_family_id]?.[item.payer] ?? true;
      const after = drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true;
      if (before !== after) n += 1;
    }));
    return n;
  })();

  const revertMatrix = () => {
    // Closing without saving reverts to the snapshot taken on open.
    try {
      const original = JSON.parse(originalMatrixRef.current || '{}');
      const revertedMaxShares = computeDrugMaxShares(original, eligibilityFamilies);
      dispatch(scenarioActions.mergeGeneratorForm({ drugPayerMatrix: original, drugMaxShares: revertedMaxShares }));
      setDrugPayerMatrix(original);
      setMatrixDirty(false);
    } catch (e) {
      // Fallback to current full matrix if parsing fails
      const full = buildFullDrugPayerMatrix();
      dispatch(scenarioActions.mergeGeneratorForm({ drugPayerMatrix: full }));
      setDrugPayerMatrix(full);
    }
    setShowEligibilityMatrix(false);
  };

  const saveMatrix = () => {
    if (!matrixDirty) return;
    // Compute max shares before closing dialog
    const newMaxShares = eligibilityFamilies.reduce<Record<string, number>>((acc, pf) => {
      const uncheckedPercent = payerMix.reduce((sum, item) => {
        const isChecked = drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true;
        return !isChecked ? sum + item.percent : sum;
      }, 0);
      // Clamp to [0, 100] and round to one decimal
      acc[pf.product_family_id] = Math.round(Math.max(0, Math.min(100, 100 - uncheckedPercent)) * 10) / 10;
      return acc;
    }, {});
    const fullMatrix = buildFullDrugPayerMatrix();
    dispatch(scenarioActions.mergeGeneratorForm({ drugPayerMatrix: fullMatrix, drugMaxShares: newMaxShares }));
    // Do NOT overwrite originalMatrixRef here; keep the pre-edit snapshot
    setMatrixDirty(false);
    setShowEligibilityMatrix(false);
    if (isDemoMode) {
      toast.current?.show({
        severity: 'info',
        summary: 'Applied for this session',
        detail: 'Read-only demo account — eligibility preferences were not saved.',
        life: 3000,
      });
      return;
    }
    if (!formData.logged_in_user) return;
    // Demo accounts are read-only server-side (423), but the edited matrix still applies to
    // this session so scenarios can be modeled with it — it just isn't persisted.
    ApiService.saveDrugPayerMatrix(formData.logged_in_user, fullMatrix)
      .then(() => notify(toast, 'Payer eligibility saved'))
      .catch((err) => {
        console.error('Failed to save drug payer matrix', err);
        toast.current?.show({
          severity: 'error',
          summary: 'Save Failed',
          detail: 'Error saving. Using current defaults.',
          life: 3500,
        });
        // Revert UI state to original matrix and recompute max shares
        try {
          const original = JSON.parse(originalMatrixRef.current || '{}');
          const revertedMaxShares = eligibilityFamilies.reduce<Record<string, number>>((acc, pf) => {
            const uncheckedPercent = payerMix.reduce((sum, item) => {
              const isChecked = original[pf.product_family_id]?.[item.payer] ?? true;
              return !isChecked ? sum + item.percent : sum;
            }, 0);
            acc[pf.product_family_id] = 100 - uncheckedPercent;
            return acc;
          }, {});
          dispatch(scenarioActions.mergeGeneratorForm({ drugPayerMatrix: original, drugMaxShares: revertedMaxShares }));
          setDrugPayerMatrix(original);
        } catch (e) {
          console.warn('Failed to revert to original matrix after save error', e);
        }
      });
  };

  // Save stays disabled until the selection differs from what was saved when the dialog opened.
  const excludedDirty = (() => {
    const before = new Set(originalExcludedOpportunitiesRef.current.map((o) => o.unique_id));
    const now = new Set(selectedExcludedOpportunities.map((o) => o.unique_id));
    return before.size !== now.size || Array.from(now).some((id) => !before.has(id));
  })();

  const cancelExcluded = () => {
    setSelectedExcludedOpportunities(originalExcludedOpportunitiesRef.current);
    setShowExcludedOpportunities(false);
  };

  const toggleExcludedOpportunity = (opp: ExcludedOpportunity, include: boolean) => {
    setSelectedExcludedOpportunities((prev) => (include
      ? (prev.some((o) => o.unique_id === opp.unique_id) ? prev : [...prev, opp])
      : prev.filter((o) => o.unique_id !== opp.unique_id)));
  };

  const excludedRows = [...areaOpportunities].sort((a, b) => {
    // Sort by contract_opportunities_id first, then by tier
    const idCompare = (a.contract_opportunities_id || 0) - (b.contract_opportunities_id || 0);
    if (idCompare !== 0) return idCompare;
    return (a.tier || 0) - (b.tier || 0);
  });

  const groupingPageCount = Math.max(1, Math.ceil(payerGroupList.length / payerGroupingPageSize));
  const groupingPage = Math.min(payerGroupingFirst, groupingPageCount - 1);
  // Payer columns keep a readable minimum width; beyond the modal's width the matrix scrolls
  // sideways inside its frame with the first three columns pinned.

  return (
    <div className="scenario-generator-container flex flex-column u-gap-4 min-w-0">
      <PageHeader
        title="New scenario run"
        subtitle="Configure the run and choose products. Optionally add your own proposed mix."
        actions={<Button variant="primary-outline" onClick={clearForm} disabled={!formHasInput || isGenerating}><i className="pi pi-times" aria-hidden="true" />Clear form</Button>}
      />

      <Card title="Configuration" className="run-config">
        <div className="run-config-grid">
          <Field label="Therapeutic area" htmlFor="category">
            <MultiSelect
              inputId="category"
              value={formData.categories || []}
              options={categoryOptions}
              onChange={(e) => handleInputChange('categories', Array.isArray(e.value) ? e.value : [])}
              placeholder={noAreasForPractice ? 'No therapeutic areas for this practice' : 'Search and select areas'}
              className="run-area-select w-full"
              invalid={!!validationErrors.category}
              disabled={!formData.logged_in_user || availableCategories.length === 0}
              filter
              filterBy="label"
              display="chip"
              filterTemplate={multiSelectSearch('Search areas')}
              panelHeaderTemplate={(options) => <div className="flex u-p-2">{options.filterElement}</div>}
            />
            {validationErrors.category && <small className="p-error">{validationErrors.category}</small>}
          </Field>

          <Field
            label="Optimization approach"
            help={generateByPayers ? APPROACH_HELP.payer : APPROACH_HELP.distribution}
            info={<>
              <Tooltip target=".approach-info-icon" position="top" />
              <i
                className="pi pi-info-circle approach-info-icon run-info-icon text-tertiary text-xs"
                data-pr-tooltip={
                  'Payer-Optimized:\n' +
                  'Most profitable drug per payer using reimbursement, rebates, and pricing\n\n' +
                  'Distribution-Based:\n' +
                  'Scenarios using overall drug mix, no payer-level optimization'
                }
                data-pr-position="top"
              />
            </>}
          >
            {/* Oral inventory can't be payer optimized: the hint is a hover tooltip on the control, as before. */}
            <Tip text={hasOralInventory ? 'Payer settings are not applicable for oral inventory categories.' : undefined} className="run-approach-tip">
            <SegmentedControl
              aria-label="Optimization approach"
              className="run-approach max-w-28rem"
              value={generateByPayers ? 'payer' : 'distribution'}
              onValueChange={(value) => {
                const mode = value === 'payer' ? 'payer' : 'distribution';
                setGenerateByPayers(mode === 'payer');
                dispatch(scenarioActions.setGeneratorState({ preferredGenerationMode: mode }));
              }}
              options={[
                { value: 'distribution', label: 'Distribution' },
                { value: 'payer', label: 'Payer optimized', disabled: !payerMixEligible || hasOralInventory },
              ]}
            />
            </Tip>
          </Field>

          <Field label="Max scenarios" htmlFor="maxScenarios" help="The generator returns up to this many scenarios">
            <InputNumber
              inputId="maxScenarios"
              value={maxScenarios}
              onValueChange={(e) => handleInputChange('max_scenarios', e.value)}
              min={1}
              max={50}
              useGrouping={false}
              className="run-max-scenarios max-w-11rem"
              invalid={!!validationErrors.max_scenarios}
            />
            {validationErrors.max_scenarios && <small className="p-error">{validationErrors.max_scenarios}</small>}
          </Field>

          {activeCategory ? (
            <CategorySizeControl
              key={activeCategory}
              category={activeCategory}
              factor={activeFactor}
              baselineUnits={activeCategoryBaselineUnits}
              showCategoryName={areaCount > 1}
              onChange={(factor) => handleCategoryScaleChange(activeCategory, factor)}
            />
          ) : (
            <Field label="Category size" help="Select a therapeutic area to size its category.">
              <span className="flex align-items-center min-h-2rem text-tertiary">—</span>
            </Field>
          )}
        </div>

        <div className="run-inputs flex flex-column u-gap-2 u-pt-4 u-px-4 u-pb-4 border-t-1 border-default">
          <span className="siq-field-label text-2xs font-medium text-secondary leading-snug">Inputs used by this run</span>
          <div className="flex flex-wrap u-gap-3">
            {generateByPayers && (
              <button type="button" className="run-tile flex-1 min-w-13rem" onClick={openEligibility} disabled={eligibilityTileDisabled}>
                <span className="flex flex-column u-gap-1 min-w-0">
                  <span className="run-tile-title text-xs font-semibold text-default">Payer eligibility</span>
                  <span className="flex align-items-center u-gap-1 text-xs text-secondary white-space-nowrap">
                    {!payerMixLoaded || !matrixReady
                      ? <><i className="pi pi-spin pi-spinner" aria-hidden="true" />Loading payer settings…</>
                      : eligibilityRestrictions > 0
                        ? <><i className="pi pi-exclamation-circle run-tile-warn text-warning" aria-hidden="true" />{plural(eligibilityRestrictions, 'restriction')}</>
                        : <><i className="pi pi-check run-tile-ok text-success" aria-hidden="true" />All eligible</>}
                  </span>
                </span>
                <i className="pi pi-chevron-right text-selected text-xs" aria-hidden="true" />
              </button>
            )}
            <button type="button" className="run-tile flex-1 min-w-13rem" onClick={openExcluded} disabled={areaOpportunities.length === 0}>
              <span className="flex flex-column u-gap-1 min-w-0">
                <span className="run-tile-title text-xs font-semibold text-default">Excluded opportunities</span>
                <span className="flex align-items-center u-gap-1 text-xs text-secondary white-space-nowrap">
                  {areaOpportunities.length === 0
                    ? <>None for this area</>
                    : areaOpportunities.length - areaIncluded > 0
                      ? <><i className="pi pi-exclamation-circle run-tile-warn text-warning" aria-hidden="true" />{areaOpportunities.length - areaIncluded} not modeled</>
                      : <><i className="pi pi-check run-tile-ok text-success" aria-hidden="true" />All included</>}
                </span>
              </span>
              <i className="pi pi-chevron-right text-selected text-xs" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Card>

      <ProductMixCard
        categories={formData.categories || []}
        activeCategory={activeCategory}
        families={activeCategoryFamilies}
        selections={selectedProductFamilies}
        proposedMixByCategory={proposedMixByCategory}
        statusByCategory={mixStatusByCategory}
        showOverrides={showOverrides}
        onToggleOverrides={setShowOverrides}
        proposedEnabled={!!(activeCategory && proposedEnabledByCategory[activeCategory])}
        onToggleProposed={setProposedEnabled}
        canReset={mixEdited}
        onReset={resetMixToCurrent}
        onCategoryChange={setActiveCategory}
        onIncludeChange={(family, include) => {
          updateProductMix(family, { include });
          pruneDisabledExcludedOpportunities({
            ...selectedProductFamilies,
            [family.product_family_name]: { ...(selectedProductFamilies[family.product_family_name] || { minDistribution: 0 }), include },
          });
        }}
        onIncludeAll={(include) => {
          // Every product in the active area, in one merge, then drop exclusions for unchecked ones.
          const patch: Record<string, Partial<ProductFamilySelection>> = {};
          activeCategoryFamilies.forEach((family) => { patch[family.product_family_name] = { include }; });
          dispatch(scenarioActions.mergeGeneratorFamilies(patch));
          const next: Record<string, ProductFamilySelection> = { ...selectedProductFamilies };
          activeCategoryFamilies.forEach((family) => {
            next[family.product_family_name] = { ...(next[family.product_family_name] || { minDistribution: 0 }), include };
          });
          pruneDisabledExcludedOpportunities(next);
        }}
        onProposedMixChange={(family, proposedDistribution) => updateProductMix(family, { proposedDistribution })}
        onMinShareChange={(family, minDistribution) => updateProductMix(family, { minDistribution })}
        onDirectRebateChange={(family, directRebatePerEu) => updateProductMix(family, { directRebatePerEu: Math.max(0, directRebatePerEu) })}
        onAdminFeeChange={(family, adminFeePerEu) => updateProductMix(family, { adminFeePerEu })}
      />

      <div className="run-footer">
        <div className="flex flex-column u-gap-2px min-w-0">
          <span className="run-footer-title text-xs font-semibold white-space-nowrap overflow-hidden text-overflow-ellipsis">{areasLabel} · {approachLabel}</span>
          <span className="run-footer-sub text-xs text-tertiary white-space-nowrap overflow-hidden text-overflow-ellipsis">Up to {maxScenarios} scenarios · {unitsLine}</span>
        </div>
        {earlierRunInProgress && (
          <span className="run-footer-warning inline-flex align-items-center u-gap-1 text-xs text-danger min-w-0"><i className="flex-none pi pi-spin pi-spinner" aria-hidden="true" />Your last run is still generating its remaining areas</span>
        )}
        {invalidMixLine && (
          <span className="run-footer-warning inline-flex align-items-center u-gap-1 text-xs text-danger min-w-0"><i className="flex-none pi pi-exclamation-circle" aria-hidden="true" />{invalidMixLine}</span>
        )}
        <span className="flex-1 min-w-0" />
        <Button className="flex-none white-space-nowrap" onClick={() => void handleGenerateScenarios(generateByPayers ? 'payer' : 'legacy')} disabled={generateDisabled}><i className="pi pi-bolt" aria-hidden="true" />{isGenerating || earlierRunInProgress ? 'Generating…' : 'Generate scenarios'}</Button>
      </div>

      {/* While generating: Ally UX Dialog that can't be closed, with an indeterminate progress bar. */}
      <Dialog
        visible={isGenerating}
        onHide={() => {}}
        showHeader={false}
        closable={false}
        closeOnEscape={false}
        draggable={false}
        resizable={false}
        size="sm"
        modal
        aria-label="Generating scenarios"
      >
        <div className="flex flex-column text-center u-gap-3 u-py-2" role="status" aria-live="polite">
          <span className="text-sm font-semibold">Generating up to {maxScenarios} scenarios…</span>
          <span className="text-xs text-tertiary">{areasLabel} · {approachLabel}</span>
          <ProgressBar mode="indeterminate" />
        </div>
      </Dialog>

      <Modal
        visible={showEligibilityMatrix}
        onHide={revertMatrix}
        title="Payer eligibility"
        subtitle={`${activeCategory ?? ''} · choose which payers each product family can be assigned to`}
        width="64.5312rem"
        className="eligibility-modal"
        height="80vh"
        beforeBody={
          <TabStrip
            tabs={[
              { key: '0', header: 'Eligibility matrix' },
              ...(!payerGroupingsLoading && payerGroupings.length > 0 ? [{ key: '1', header: 'Payer groupings' }] : []),
            ]}
            active={String(payerDialogActiveTabIndex)}
            onChange={(key) => setPayerDialogActiveTabIndex(Number(key))}
          />
        }
        footerStart={payerDialogActiveTabIndex === 0
          ? (matrixChangeCount > 0 ? plural(matrixChangeCount, 'unsaved change') : 'All payers are eligible unless unchecked')
          : undefined}
        footer={payerDialogActiveTabIndex === 0 ? <>
          <Button variant="primary-outline" onClick={revertMatrix}>Cancel</Button>
          <Button onClick={saveMatrix} disabled={!matrixDirty}>Save</Button>
        </> : <>
          <Button variant="primary-outline" onClick={() => { revertMatrix(); navigate('/account-settings'); }}>Edit in setup</Button>
          <Button variant="primary-outline" onClick={revertMatrix}>Close</Button>
        </>}
      >
        {payerDialogActiveTabIndex === 0 ? (
          <div className="flex flex-column u-gap-3">
            <div className="elig-toolbar flex justify-content-between align-items-center u-gap-4 flex-wrap">
              <TextHelper className="flex-1 min-w-15rem">Max market share is the share of patients a family can reach given its eligible payers. Products not used in the mix are greyed out.</TextHelper>
              <label className="inline-flex align-items-center u-gap-2 text-xs text-secondary cursor-pointer">
                <InputSwitch
                  inputId="toggle-effective-share"
                  checked={showEffectiveShare}
                  onChange={(e) => setShowEffectiveShare(e.value)}
                />
                <span>Show effective share</span>
                <Tooltip target=".effective-share-info-icon" position="top" />
                <i
                  className="pi pi-info-circle effective-share-info-icon run-info-icon text-tertiary text-xs"
                  data-pr-tooltip={"The payer's share of a given product's market opportunity, taking into consideration each payer's formulary restrictions."}
                />
              </label>
            </div>
            <Tooltip position="top" target=".payer-grouping-link" />
            <EligibilityMatrix
              families={eligibilityFamilies}
              payers={visiblePayerMix}
              matrix={drugPayerMatrix}
              isIncluded={(pf) => selectedProductFamilies[pf.product_family_name]?.include ?? true}
              ready={matrixReady}
              showEffectiveShare={showEffectiveShare}
              allSelected={isEntireMatrixSelected}
              anySelected={eligibilityFamilies.some((pf) => (selectedProductFamilies[pf.product_family_name]?.include ?? true) && visiblePayerMix.some((item) => drugPayerMatrix[pf.product_family_id]?.[item.payer] ?? true))}
              onToggleAll={toggleAllMatrix}
              onToggleRow={toggleRowAll}
              onToggleCell={(pfId, payer, next) => {
                setDrugPayerMatrix((prev) => {
                  if (prev[pfId]?.[payer] !== next) setMatrixDirty(true);
                  return { ...prev, [pfId]: { ...(prev[pfId] || {}), [payer]: next } };
                });
              }}
              hasGrouping={(payer) => payersInGroupings.has(payer.trim().toLowerCase())}
              onJumpToGrouping={jumpToPayerGrouping}
            />
          </div>
        ) : (
          <PayerGroupingsTable
            groups={payerGroupList}
            page={groupingPage}
            pageSize={payerGroupingPageSize}
            onPagination={(next) => { setPayerGroupingFirst(next.pageIndex); setPayerGroupingPageSize(next.pageSize); }}
            highlight={payerGroupingHighlight}
          />
        )}
      </Modal>

      <Modal
        visible={showExcludedOpportunities}
        onHide={cancelExcluded}
        title="Excluded opportunities"
        subtitle="These rebates can't be modeled automatically. Check one to include it in this run."
        width="61.25rem"
        footerStart={areaIncluded > 0 ? `${plural(areaIncluded, 'opportunity', 'opportunities')} will be included` : 'None included'}
        footer={<>
          <Button variant="primary-outline" onClick={cancelExcluded}>Cancel</Button>
          <Button onClick={() => applyOpportunities(selectedExcludedOpportunities)} disabled={!excludedDirty}>Save</Button>
        </>}
      >
        <Tooltip position="top" key={`excl-opp-tooltip-${selectedExcludedOpportunities.map(o => o.unique_id).join(',')}`} target=".excl-opp-cell" />
        <ExcludedOpportunitiesTable
          rows={excludedRows}
          isSelectable={isExcludedOpportunitySelectable}
          isChecked={(opp) => selectedExcludedOpportunities.some((o) => o.unique_id === opp.unique_id)}
          onToggle={toggleExcludedOpportunity}
          withTooltip={wrapWithOpportunityTooltip}
        />
      </Modal>
    </div>
  );
};

export default ScenarioGenerator;
